import { type Object3D } from 'three';

import { publish } from '@pkg/components/Sections/Profile/pubSub';
import {
    glTouchEndTopic,
    glTouchLeaveTopic,
    glTouchMoveTopic,
    glTouchStartTopic,
} from '@pkg/components/Sections/Profile/pubSub/constants';

import { isTouchDevice } from '../utils';

export interface EventData {
    normalized: {
        x: number;
        y: number;
    };
    raw: {
        x: number;
        y: number;
    };
    rawNormalized: {
        x: number;
        y: number;
    };
}

export default class EventListeners {
    public width: number;
    public height: number;
    public parentElement?: HTMLElement | null;
    mouse: EventData | null;
    touch?: EventData | null;
    scene: Object3D;
    isTouchDevice: boolean;

    constructor(scene) {
        this.scene = scene;
        this.mouse = null;
        this.touch = null;
        this.width = 0;
        this.height = 0;

        this.isTouchDevice = isTouchDevice();
    }

    createListeners(domElement: HTMLElement) {
        this.parentElement = domElement;

        if (this.isTouchDevice) {
            this.parentElement = document.body;
            this._attachListenersMobile(this.parentElement);
        } else {
            this._attachListenersDesktop(this.parentElement);
        }
    }

    _attachListenersDesktop(el: HTMLElement) {
        el.addEventListener('mousedown', this.onMouse.bind(this), { passive: true });
        el.addEventListener('mouseup', this.onMouseEnd.bind(this), { passive: true });
        el.addEventListener('mousemove', this.onMouseMove.bind(this), { passive: false });
        el.addEventListener('mouseleave', this.onMouseLeave.bind(this), { passive: true });
    }

    _attachListenersMobile(el: HTMLElement) {
        el.addEventListener('touchstart', this.onTouch.bind(this), { passive: true });
        el.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
        el.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
    }

    removeListeners() {
        if (!this.parentElement) return;

        if (this.isTouchDevice) {
            this.parentElement.removeEventListener('touchstart', this.onTouch.bind(this));
            this.parentElement.removeEventListener('touchend', this.onTouchEnd.bind(this));
            this.parentElement.removeEventListener('touchmove', this.onTouchMove.bind(this));
        } else {
            this.parentElement.removeEventListener('mousedown', this.onMouse.bind(this));
            this.parentElement.removeEventListener('mouseup', this.onMouseEnd.bind(this));
            this.parentElement.removeEventListener('mousemove', this.onMouseMove.bind(this));
        }

        this.parentElement = null;
    }

    onMouse(ev: MouseEvent) {
        this.mouse = this.getMouse(ev);
        publish(glTouchStartTopic, this.mouse);
    }

    onMouseEnd(ev: MouseEvent) {
        if (!this.scene.visible) return;
        this.mouse = this.getMouse(ev);
        publish(glTouchEndTopic, this.mouse);
    }

    onMouseLeave(ev: MouseEvent) {
        if (!this.scene.visible) return;
        this.mouse = this.getMouse(ev);
        publish(glTouchLeaveTopic, this.mouse);
    }

    onTouch(ev: TouchEvent) {
        if (!this.scene.visible) return;
        this.touch = this.getTouch(ev);
        publish(glTouchStartTopic, this.touch);
    }

    onTouchEnd(ev: TouchEvent) {
        if (!this.scene.visible) return;
        this.touch = this.getTouch(ev);
        publish(glTouchEndTopic, this.touch);
    }

    onMouseMove(ev: MouseEvent) {
        if (!this.scene.visible) return;
        this.mouse = this.getMouse(ev);
        publish(glTouchMoveTopic, this.mouse);
    }

    onTouchMove(ev: TouchEvent) {
        if (!this.scene.visible) return;
        if (ev.touches) {
            if (ev.touches.length > 1) {
                return;
            }
        }

        this.touch = this.getTouch(ev);

        publish(glTouchMoveTopic, this.touch);
    }

    onTouchLeave(ev: TouchEvent) {
        if (!this.scene.visible) return;
        this.touch = this.getTouch(ev);
        publish(glTouchEndTopic, this.touch);
    }

    getTouch(ev: TouchEvent) {
        if (!ev.changedTouches.length) return;

        const event = ev.changedTouches[0];

        let width = 0;
        let height = 0;

        if (this.parentElement) {
            width = this.parentElement.clientWidth;
            height = this.parentElement.clientHeight;
        } else {
            width = this.width;
            height = this.height;
        }

        return {
            normalized: {
                x: (event.clientX / width) * 2 - 1,
                y: -(event.clientY / height) * 2 + 1,
            },
            raw: {
                x: event.clientX,
                y: event.clientY,
            },
            rawNormalized: {
                x: (event.clientX - width * 0.5) * 2,
                y: (event.clientY - height * 0.5) * 2,
            },
        };
    }

    getMouse(ev: MouseEvent) {
        let width = 0;
        let height = 0;

        if (this.parentElement) {
            width = this.parentElement.offsetWidth;
            height = this.parentElement.offsetHeight;
        } else {
            width = this.width;
            height = this.height;
        }

        return {
            normalized: {
                x: (ev.offsetX / width) * 2 - 1,
                y: -(ev.offsetY / height) * 2 + 1,
            },
            raw: {
                x: ev.offsetX,
                y: ev.offsetY,
            },
            rawNormalized: {
                x: (ev.offsetX - width * 0.5) * 2,
                y: (ev.offsetY - height * 0.5) * 2,
            },
        };
    }
}
